import { Dispatch, SetStateAction } from "react";
import IconCard from "../../../views/workflows/IconCard";
import { EmailBuilderPages, EmailTemplateTypes } from "./EmailBuilderTypes";
import { Input } from "reactstrap";
import RequiredTitle from "../../../elements/RequiredTitle";
import { useAtomValue } from "jotai";
import { isGlobalCloudAtom } from "../../../atoms/GlobalAtoms";
import SkeletonLoader from "../../../elements/SkeletonLoader";
import { SelectPicker } from "rsuite";
import useGlobalApps from "../../../api/api-hooks/globalApps/useGlobalApps";

interface SelectionProps {
  templateType: EmailTemplateTypes | "";
  setTemplateType: Dispatch<SetStateAction<EmailTemplateTypes | "">>;
  templateName: string;
  setTemplateName: Dispatch<SetStateAction<string>>;
  templateDescription: string;
  setTemplateDescription: Dispatch<SetStateAction<string>>;
  setPage: Dispatch<SetStateAction<EmailBuilderPages>>;
  fromName: string;
  setFromName: Dispatch<SetStateAction<string>>;
  fromEmail: string;
  setFromEmail: Dispatch<SetStateAction<string>>;
  subject: string;
  setSubject: Dispatch<SetStateAction<string>>;
  globalAppID: string;
  setGlobalAppID: Dispatch<SetStateAction<string>>;
}

function SelectionPage({
  templateType,
  setTemplateType,
  templateName,
  setTemplateName,
  templateDescription,
  setTemplateDescription,
  fromName,
  setFromName,
  fromEmail,
  setFromEmail,
  subject,
  setSubject,
  globalAppID,
  setGlobalAppID,
  setPage,
}: SelectionProps) {
  const isGlobal = useAtomValue(isGlobalCloudAtom);

  return (
    <>
      <RequiredTitle title="Email Template Name" size="lg" />
      <Input
        placeholder="ex: Event Invitation Template"
        value={templateName}
        onChange={(e) => setTemplateName(e.target.value)}
        className="mb-4"
      />
      <RequiredTitle title="From Name" size="lg" />
      <Input
        placeholder="ex: GrowthEvents"
        value={fromName}
        onChange={(e) => setFromName(e.target.value)}
        className="mb-4"
      />
      <RequiredTitle title="From Email" size="lg" />
      <Input
        placeholder="ex: noreply@growthevents.com"
        value={fromEmail}
        onChange={(e) => setFromEmail(e.target.value)}
        className="mb-4"
      />
      <RequiredTitle title="Email Subject" size="lg" />
      <Input
        placeholder="ex: Upcoming Event"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        className="mb-4"
      />
      <h4>Description</h4>
      <Input
        placeholder="ex: This email is used for sending event invitations. Ensure you are using an event record"
        value={templateDescription}
        type="textarea"
        onChange={(e) => setTemplateDescription(e.target.value)}
        rows={3}
        className="mb-4"
      />
      {isGlobal && (
        <GlobalAppSelection
          globalAppID={globalAppID}
          setGlobalAppID={setGlobalAppID}
        />
      )}
      <RequiredTitle title="Template Type" size="lg" />
      {/* @ts-ignore */}
      <IconCard
        icon="mdi mdi-email-sync"
        name="Dynamic"
        description="An email that uses a sendgrid template."
        isSelected={templateType === "Dynamic"}
        onClick={() => {
          setTemplateType("Dynamic");
          templateName && setPage("Dynamic");
        }}
        css="mb-2"
      />
      {/* @ts-ignore */}
      <IconCard
        icon="mdi mdi-email-plus-outline"
        name="Custom"
        description="An email that doesn't use a sendgrid template."
        isSelected={templateType === "Custom"}
        onClick={() => {
          setTemplateType("Custom");
          templateName && setPage("Custom");
        }}
      />
    </>
  );
}

interface AppSelectionProps {
  globalAppID: string;
  setGlobalAppID: Dispatch<SetStateAction<string>>;
}

function GlobalAppSelection({
  globalAppID,
  setGlobalAppID,
}: AppSelectionProps) {
  const { globalApps, loadingApps: loading } = useGlobalApps();

  return (
    <>
      <h4 className="">Associated Global App (Optional)</h4>
      {loading && <SkeletonLoader height={42} css="mb-4" />}
      {!loading && (
        <SelectPicker
          data={globalApps}
          preventOverflow
          labelKey="Name"
          valueKey="id"
          value={globalAppID}
          onChange={(appID) => {
            setGlobalAppID(appID || "");
          }}
          block
          placeholder="Select Global App"
          size="lg"
          className="mb-4"
        />
      )}
    </>
  );
}

export default SelectionPage;
